import React from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import OpportunityListingCard from './OpporunityCard';
import ListingWrapper from './ListingWrapper';
import { useStoreSelector } from '@ui/hooks/useStoreSelector';
import useOpportunitiesStore from '../store/useOpportunitiesStore';
import OpportunityFilters from './OpportunityFilters';
import { cn } from '@ui/atoms/utils/utils';
import { Text } from '@ui/atoms';
import { useSearchParams } from 'next/navigation';
import {
    getAppliedFilters,
    isAdminDeal,
    isBondsView,
    isFestiveDeal,
    isSdiView,
    isTBillsView,
} from '../utils/utils';
import {
    DESKTOP_LIMIT,
    MOBILE_LIMIT,
    OPPORTUNITY_BOND_TYPES,
    OPPORTUNITY_STATUS,
    SDI_TABS,
} from '../utils/constants';
import useSyncFilters from '../hooks/useSyncFilters';
import OpportunitySkeleton from '@components/OpportunityListing/components/LoadersAndEmptyStates/OpportunitySkeleton';
import OpportunityListingEmptyState from '@components/OpportunityListing/components/LoadersAndEmptyStates/OpportunityListingEmptyState';
import { ErrorHandler } from './ErrorHandler';
import {
    isUpcomingOpportunity,
    opportunityClosedStates,
} from '@helpers/constants';
import { getScrollThreshold } from '../utils/helper';
import useWindowDimensions from '@hooks/useWindowDimensions';
import RmConsultationBanner from '@components/OpportunityListing/components/RmConsultationBanner';

const OpportunityListingResults = ({
    showActiveOpportunities = true,
    activities,
    showFestiveDays,
}) => {
    const { width } = useWindowDimensions();
    const searchParams = useSearchParams();
    const activeTab = searchParams.get('type') || OPPORTUNITY_BOND_TYPES.BOND;
    const { updateQueryParams, clearAllFilters } = useSyncFilters();
    const queryParams = Object.fromEntries(searchParams.entries());
    const { count: filterCount } = getAppliedFilters(queryParams);
    const {
        data,
        count,
        isFetching,
        error,
        fetchOpportunities,
        page,
        status,
        isAuthorized,
    } = useStoreSelector(useOpportunitiesStore, (state) => ({
        data: state.data.rows,
        count: state.data.count,
        isFetching: state.isFetching,
        error: state.error,
        fetchOpportunities: state.fetchOpportunities,
        page: state.page,
        stats: state.stats,
        status: state.status,
        isAuthorized: state.isAuthorized,
    }));

    const fetchMoreData = async () => {
        try {
            const queryParams = Object.fromEntries(searchParams.entries());
            await fetchOpportunities({
                ...queryParams,
                status,
                page: Number(page) + 1,
                limit: width < 1280 ? MOBILE_LIMIT : DESKTOP_LIMIT,
            });
        } finally {
        }
    };

    const interestRecorded = (opportunityId) => {
        return activities?.length
            ? activities?.find((item) => item.opportunityId === opportunityId)
            : null;
    };

    return (
        <ListingWrapper
            className="w-full min-h-screen bg-basicWhite flex"
            tag="main"
            wrapperClassName={'flex flex-col justify-between flex-1'}
        >
            <div className="px-4 py-6  md:px-0 md:py-10 md:pt-6">
                <div className="flex flex-col gap-6">
                    {isBondsView(activeTab) &&
                        status !== OPPORTUNITY_STATUS.CLOSED ? (
                        <OpportunityFilters
                            updateQueryParams={updateQueryParams}
                            opportunitiesCount={count}
                            clearAllFilters={clearAllFilters}
                        />
                    ) : null}

                    {isSdiView(activeTab) && status !== OPPORTUNITY_STATUS.CLOSED ? (
                        <Text content={"The following Securitised Debt Instruments (SDIs) are regulated by RBI"} className="p4-regular text-gray-600" />
                    ) : null}


                    {isFetching && data.length === 0 ? (
                        <div className="grid grid-cols-1 gap-6 md:gap-10 md:grid-cols-2 xl:grid-cols-3">
                            {Array.from({ length: 6 }, (_, i) => i + 1).map(
                                (item) => (
                                    <OpportunitySkeleton
                                        key={item}
                                        type={activeTab}
                                    />
                                )
                            )}
                        </div>
                    ) : !isFetching && data.length === 0 ? (
                        error ? (
                            <ErrorHandler
                                data={data}
                                error={error}
                                loading={isFetching}
                                isFetchingActiveOpportunities={
                                    showActiveOpportunities
                                }
                                hasFilters={filterCount > 0}
                            />
                        ) : (
                            <div className="mx-auto mt-2 col-span-full md:mt-4">
                                <OpportunityListingEmptyState
                                    isActiveOpportunities={true}
                                    hasFilters={filterCount}
                                    type={activeTab}
                                />
                            </div>
                        )
                    ) : (
                        <InfiniteScroll
                            dataLength={data.length}
                            next={fetchMoreData}
                            hasMore={data.length < count}
                            loader={
                                isFetching && (
                                    <div className="flex items-center justify-center gap-3 py-3">
                                    <div className={'button-loader-primary '} />
                                        <Text
                                            content={'Loading results'}
                                            className={cn('py-4 text-gray-500')}
                                        />
                                    </div>
                                )
                            }
                            endMessage={''}
                            scrollThreshold={getScrollThreshold(
                                isAuthorized,
                                width
                            )}
                            style={{ overflow: 'unset' }}
                        >
                            <div className="grid grid-cols-1 gap-6 md:gap-10 md:grid-cols-2 xl:grid-cols-3">
                                {data.map((opportunity, index) => (
                                    <OpportunityListingCard
                                        opportunity={opportunity}
                                        type={activeTab}
                                        key={`${opportunity.id}-${index}`}
                                        interestRecorded={interestRecorded(
                                            opportunity?.id
                                        )}
                                        isFestiveDeal={isFestiveDeal(
                                            isAuthorized,
                                            showFestiveDays,
                                            opportunity,
                                            !isTBillsView(opportunity?.type)
                                        )}
                                        isAdminDeal={isAdminDeal(
                                            isAuthorized,
                                            opportunity
                                        )}
                                        isUpcoming={isUpcomingOpportunity(
                                            opportunity
                                        )}
                                        isClosed={opportunityClosedStates.includes(
                                            opportunity.state
                                        )}
                                        isAuthorized={isAuthorized}
                                    />
                                ))}
                            </div>
                            {error ? (
                                <ErrorHandler
                                    data={data}
                                    error={error}
                                    loading={isFetching}
                                    isFetchingActiveOpportunities={
                                        showActiveOpportunities
                                    }
                                    hasFilters={filterCount > 0}
                                />
                            ) : null}
                        </InfiniteScroll>
                    )}
                </div>
            </div>
            {isAuthorized && status !== OPPORTUNITY_STATUS.CLOSED ? <RmConsultationBanner /> : null}
        </ListingWrapper>
    );
};

export default OpportunityListingResults;
